import {useCallback} from 'react'

import {useEnabledFeatures} from '../../../hooks/use-enabled-features'
import {getGroupIdFromQueryKey, type GroupId} from './query-keys'
import {usePaginatedMemexItemsQuery} from './use-paginated-memex-items-query'

export function useHasMoreItems() {
  const {memex_table_without_limits} = useEnabledFeatures()

  // If memex_table_without_limits is disabled, we want to always
  // return a callback that simply returns false.

  // eslint-disable-next-line react-hooks/rules-of-hooks
  if (!memex_table_without_limits) return useCallback(() => false, [])
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {hasNextPage, hasNextPageForGroup, queryKeysForItems} = usePaginatedMemexItemsQuery()

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useCallback(
    (groupId?: GroupId) => {
      // If a `groupId` is passed, we can just call `hasNextPageForGroup directly.
      if (groupId != null) {
        return hasNextPageForGroup(groupId)
      }

      // If the data isn't grouped, we can simply check the `hasNextPage` value which should indicate
      // whether or not there is more data on the server.
      if (hasNextPage) {
        return true
      }

      // Otherwise, we need to check individual groups to see if at least one of them has more data on the server
      // We extract the groupId from each query key and see if it has a next page.
      const groupIds = new Set<string>()
      for (const queryKeyForItem of queryKeysForItems) {
        const groupIdFromQueryKey = getGroupIdFromQueryKey(queryKeyForItem)
        if (groupIdFromQueryKey != null) {
          if (!groupIds.has(groupIdFromQueryKey) && hasNextPageForGroup(groupIdFromQueryKey)) {
            return true
          }
          // Go ahead and add the groupId to a set so that we don't check it again.
          groupIds.add(groupIdFromQueryKey)
        }
      }

      return false
    },
    [hasNextPage, hasNextPageForGroup, queryKeysForItems],
  )
}
